// WalletShared.jsx — shared helpers, hooks, and UI atoms used across all wallet pages
import { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Check, X, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import walletController from '../../controllers/walletController';

// ─── Format helpers ───────────────────────────────────────────────────────────
export const fmtINR   = (n) => new Intl.NumberFormat('en-IN',{ style:'currency', currency:'INR', maximumFractionDigits:0 }).format(n||0);
export const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-IN',{ day:'2-digit', month:'short', year:'numeric' }) : '—';
export const fmtDT    = (d) => d ? new Date(d).toLocaleString('en-IN',{ day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
export const maskCard = (n) => n ? `•••• •••• •••• ${String(n).slice(-4)}` : '•••• •••• •••• ••••';
export const maskAcct = (n) => n ? `${'•'.repeat(Math.max(0,String(n).length-4))}${String(n).slice(-4)}` : '••••••••';

// ─── Spinner ──────────────────────────────────────────────────────────────────
export const Spinner = ({ size=16 }) => (
  <Loader2 size={size} style={{ display:'inline', animation:'spin 1s linear infinite' }}/>
);

// ─── Banner ───────────────────────────────────────────────────────────────────
export const Banner = ({ type, message, onClose }) => (
  <div style={{ background:type==='error'?'#fef2f2':'#f0fdf4', border:`1px solid ${type==='error'?'#fca5a5':'#86efac'}`, borderRadius:8, padding:'10px 14px', marginBottom:14, display:'flex', alignItems:'center', gap:8, color:type==='error'?'#b91c1c':'#15803d', fontSize:13 }}>
    {type==='error'?<AlertCircle size={14}/>:<Check size={14}/>}
    <span style={{ flex:1 }}>{message}</span>
    <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', padding:0, display:'flex' }}><X size={13}/></button>
  </div>
);

// ─── StatCard ─────────────────────────────────────────────────────────────────
export const StatCard = ({ label, value, sub, trend, color='#1e293b', icon: Icon }) => (
  <div style={{ background:'#fff', borderRadius:10, padding:'14px 16px', border:'1px solid #f1f5f9' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
      <span style={{ fontSize:11, color:'#64748b', fontWeight:500 }}>{label}</span>
      {Icon && <Icon size={16} color="#94a3b8"/>}
    </div>
    <div style={{ fontSize:20, fontWeight:700, color }}>{value}</div>
    {sub   && <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>{sub}</div>}
    {trend !== undefined && (
      <div style={{ display:'flex', alignItems:'center', gap:3, marginTop:4, fontSize:11, color: trend>=0 ? '#15803d' : '#b91c1c' }}>
        {trend>=0 ? <TrendingUp size={11}/> : <TrendingDown size={11}/>}
        {Math.abs(trend)}% vs last month
      </div>
    )}
  </div>
);

// ─── PageShell ────────────────────────────────────────────────────────────────
export const PageShell = ({ title, subtitle, icon:Icon, actions, children, banner, onCloseBanner }) => (
  <div style={{ padding:24, backgroundColor:'#f8fafc', minHeight:'100vh' }}>
    <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
      <div>
        <h1 style={{ fontSize:22, fontWeight:700, color:'#1e293b', margin:'0 0 4px', display:'flex', alignItems:'center', gap:8 }}>
          {Icon && <Icon size={22} color="#3b82f6"/>}{title}
        </h1>
        {subtitle && <p style={{ fontSize:13, color:'#64748b', margin:0 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display:'flex', gap:8 }}>{actions}</div>}
    </div>
    {banner && <Banner type={banner.type} message={banner.message} onClose={onCloseBanner}/>}
    {children}
  </div>
);

// ─── EmptyState ───────────────────────────────────────────────────────────────
export const EmptyState = ({ icon:Icon, message, action }) => (
  <div style={{ textAlign:'center', padding:'60px 0', color:'#94a3b8' }}>
    {Icon && <Icon size={44} color="#e2e8f0" style={{ marginBottom:12 }}/>}
    <p style={{ fontSize:14, margin:'0 0 16px' }}>{message}</p>
    {action}
  </div>
);

// ─── Modal shell ──────────────────────────────────────────────────────────────
export const Modal = ({ title, onClose, children, footer, maxWidth=520 }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1010, padding:16 }}>
    <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth, maxHeight:'92vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,.2)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:'1px solid #f1f5f9' }}>
        <h3 style={{ fontSize:16, fontWeight:700, color:'#1e293b', margin:0 }}>{title}</h3>
        <button onClick={onClose} style={{ background:'none', border:'1px solid #e2e8f0', borderRadius:6, width:30, height:30, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#64748b' }}><X size={16}/></button>
      </div>
      <div style={{ padding:'16px 20px', overflowY:'auto', flex:1, display:'flex', flexDirection:'column', gap:14 }}>{children}</div>
      {footer && <div style={{ padding:'12px 20px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'flex-end', gap:8, background:'#fafafa' }}>{footer}</div>}
    </div>
  </div>
);

// ─── Form atoms ───────────────────────────────────────────────────────────────
export const FG    = ({ label, children, hint }) => (
  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
    {label && <label style={{ fontSize:11, fontWeight:600, color:'#475569', textTransform:'uppercase', letterSpacing:'0.4px' }}>{label}</label>}
    {children}
    {hint && <span style={{ fontSize:10, color:'#94a3b8' }}>{hint}</span>}
  </div>
);
export const INP   = (props) => <input  {...props} style={{ padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:7, fontSize:13, color:'#1e293b', outline:'none', background:'#fff', width:'100%', boxSizing:'border-box', ...props.style }}/>;
export const SEL   = ({ children, ...props }) => <select {...props} style={{ padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:7, fontSize:13, color:'#1e293b', outline:'none', background:'#fff', width:'100%', boxSizing:'border-box' }}>{children}</select>;
export const AREA  = (props) => <textarea {...props} style={{ padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:7, fontSize:13, color:'#1e293b', outline:'none', background:'#fff', width:'100%', boxSizing:'border-box', resize:'vertical', ...props.style }}/>;
export const Row2  = ({ children }) => <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>{children}</div>;
export const Row3  = ({ children }) => <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>{children}</div>;
export const BtnPrimary = ({ children, onClick, disabled, style={} }) => (
  <button onClick={onClick} disabled={disabled} style={{ padding:'8px 18px', background:disabled?'#94a3b8':'#1e293b', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:disabled?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:5, ...style }}>{children}</button>
);
export const BtnGhost = ({ children, onClick, style={} }) => (
  <button onClick={onClick} style={{ padding:'8px 16px', background:'transparent', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', color:'#475569', ...style }}>{children}</button>
);
export const BtnDanger = ({ children, onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled} style={{ padding:'8px 16px', background:'#ef4444', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:disabled?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:5 }}>{children}</button>
);

// ─── useBanner hook ───────────────────────────────────────────────────────────
export const useBanner = () => {
  const [banner, setBanner] = useState(null);
  const notify = useCallback((type, message) => {
    setBanner({ type, message });
    setTimeout(() => setBanner(null), 4000);
  }, []);
  return { banner, notify, clearBanner: () => setBanner(null) };
};

// ─── useWalletUser hook ───────────────────────────────────────────────────────
export const useWalletUser = () => {
  const user = walletController.getCurrentUser();
  return { user, userId: user?.id || user?._id, isSuperAdmin: walletController.isSuperAdmin() };
};

export default walletController;